/**
 * Polls: a question with two to ten answers, attached to a message. Bots post them; people vote in the app.
 * Votes are counted per answer and are visible to the bot that posted the poll (and to bots listening to the
 * channel), never to other people.
 */
import { z } from 'zod';

export const POLL_LIMITS = {
  question: 300,
  answers: { min: 2, max: 10 },
  answerText: 55,
  /** A poll lasts between an hour and 32 days; one hour is the shortest so that people have time to see it. */
  minHours: 1,
  maxHours: 24 * 32,
  defaultHours: 24,
} as const;

export interface PollAnswer {
  /** 1 to 10, in the order the bot gave them. */
  id: number;
  text: string;
  /** How many people chose it. */
  count: number;
}

export interface Poll {
  question: string;
  answers: PollAnswer[];
  /** People may pick several answers (otherwise one). */
  allowMultiselect: boolean;
  /** When voting closes, unless the poll is ended earlier. */
  expiresAt: string;
  /** Voting is closed: it ran out of time, or was ended. Results are final. */
  ended: boolean;
  /** How many people voted (each counted once, however many answers they chose). */
  totalVoters: number;
  /** The answers the viewer chose, when the viewer is known. Left out of events sent to everyone. */
  myVotes?: number[];
}

/** What a bot sends to start a poll. */
export const pollInputSchema = z
  .object({
    question: z.string().trim().min(1, 'A poll needs a question').max(POLL_LIMITS.question),
    answers: z
      .array(z.string().trim().min(1, 'Answers cannot be empty').max(POLL_LIMITS.answerText))
      .min(POLL_LIMITS.answers.min, `A poll needs at least ${POLL_LIMITS.answers.min} answers`)
      .max(POLL_LIMITS.answers.max, `A poll can have at most ${POLL_LIMITS.answers.max} answers`),
    allowMultiselect: z.boolean().default(false),
    durationHours: z.number().int().min(POLL_LIMITS.minHours).max(POLL_LIMITS.maxHours).default(POLL_LIMITS.defaultHours),
  })
  .superRefine((p, c) => {
    const seen = new Set<string>();
    p.answers.forEach((a, i) => {
      const key = a.toLowerCase();
      if (seen.has(key)) c.addIssue({ code: 'custom', path: ['answers', i], message: `Two answers are the same: "${a}"` });
      seen.add(key);
    });
  });

export type PollInput = z.input<typeof pollInputSchema>;
export type PollData = z.output<typeof pollInputSchema>;

/** Someone's choices on a poll: every answer they want, or none to take their vote back. */
export const pollVoteSchema = z.object({
  answerIds: z.array(z.number().int().min(1).max(POLL_LIMITS.answers.max)).max(POLL_LIMITS.answers.max),
});

/** Sent to everyone who can see the channel when votes change or a poll ends: counts only, never who voted. */
export interface PollUpdate {
  channelId: string;
  messageId: string;
  spaceId: string | null;
  answers: { id: number; count: number }[];
  totalVoters: number;
  ended: boolean;
}

/** Who voted for what, as bots hear it. */
export interface BotPollVote {
  messageId: string;
  channelId: string;
  spaceId: string;
  userId: string;
  answerId: number;
}

/** A poll closed, because its time ran out or it was ended. */
export interface BotPollEnded {
  messageId: string;
  channelId: string;
  spaceId: string;
  answers: { id: number; text: string; count: number }[];
  totalVoters: number;
}
